import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useEligibleProcessors } from "@/services/admin/merchant-processors"
import type { MerchantProcessor } from "@/types/admin-merchant-processor"
import type { ProcessorConfigItem } from "@/types/admin-processor-config"

const prefix = "pages.adminProcessorConfig"

interface ProcessorSelectCellProps {
  item: ProcessorConfigItem
  /** Id do trigger, para o <Label> da linha apontar para ele. */
  triggerId?: string
  disabled?: boolean
  onSelect: (
    item: ProcessorConfigItem,
    processor: MerchantProcessor
  ) => void
}

/**
 * Combo da linha do meio de pagamento: mostra a credencial que processa e permite trocá-la.
 *
 * É componente próprio, e não JSX dentro do `map` do card, porque chama hooks — um por linha,
 * o que dentro do `map` seria chamada condicional de hook.
 *
 * A lista de elegíveis só é buscada quando o combo abre: a página tem uma linha por método, e
 * buscar de antemão seria uma requisição por linha para dados que o operador talvez nunca veja.
 */
export const ProcessorSelectCell = ({
  item,
  triggerId,
  disabled,
  onSelect,
}: ProcessorSelectCellProps) => {
  const { t } = useTranslation()
  const [wasOpened, setWasOpened] = useState(false)

  const {
    data: processors = [],
    isLoading,
    isError,
    refetch,
  } = useEligibleProcessors(
    item.whiteLabelId,
    item.method,
    wasOpened
  )

  const methodLabel = t(
    `${prefix}.methods.${item.method}`,
    {
      defaultValue: item.method,
    }
  )
  const currentId = item.processor?.id ?? ""
  const currentLabel =
    item.processor?.name ??
    item.processor?.id ??
    t(`${prefix}.processorUnresolved`)

  const handleChange = (nextId: string) => {
    if (nextId === currentId) return
    const next = processors.find(
      (processor) => processor.id === nextId
    )
    if (next) onSelect(item, next)
  }

  return (
    <Select
      value={currentId}
      disabled={disabled}
      onOpenChange={(open) => {
        if (open) setWasOpened(true)
      }}
      onValueChange={handleChange}
    >
      <SelectTrigger
        id={triggerId}
        className="h-8 w-full min-w-0 max-w-[15rem] text-xs"
        aria-label={t(`${prefix}.processorAria`, {
          method: methodLabel,
        })}
      >
        {/* O placeholder cobre o caso sem credencial resolvida: `value=""` não casa
            com nenhum item, e o SelectValue ficaria mudo. */}
        <SelectValue placeholder={currentLabel}>
          {currentLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {isLoading && (
          <div role="presentation" className="p-2">
            <Skeleton
              role="status"
              aria-live="polite"
              aria-label={t(`${prefix}.loadingProcessors`)}
              className="h-6 w-40"
            />
          </div>
        )}
        {/* Erro NÃO é vazio: dizer "nenhuma credencial atende" quando a consulta
            caiu faria o operador concluir que precisa cadastrar uma credencial
            que já existe — numa tela de roteamento de dinheiro. */}
        {!isLoading && isError && (
          <div role="presentation" className="p-2">
            <p
              role="status"
              aria-live="polite"
              className="text-xs text-red-700"
            >
              {t(`${prefix}.processorsLoadError`)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 h-7 w-full text-xs"
              onClick={() => refetch()}
            >
              {t(`${prefix}.retry`)}
            </Button>
          </div>
        )}
        {!isLoading &&
          !isError &&
          processors.length === 0 && (
            <p
              role="status"
              aria-live="polite"
              className="px-2 py-3 text-xs text-muted-foreground"
            >
              {t(`${prefix}.noEligibleProcessors`)}
            </p>
          )}
        {processors.map((processor) => (
          <SelectItem
            key={processor.id}
            value={processor.id}
            className="text-xs"
          >
            {processor.name ?? processor.id}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
